import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube, MapPin, Phone, Mail, ArrowRight } from 'lucide-react';
import { XLogo } from './XLogo';
import { toast } from 'sonner';
import { SITE } from '../data/mock';
import { api } from '../lib/api';
import { usePublicSettings } from '../hooks/usePublicSettings';
import { InlineText } from './InlineEditable';

export default function Footer() {
  const settings = usePublicSettings();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.subscribeNewsletter(email);
      toast.success('Aboneliğiniz için teşekkürler!');
      setEmail('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Abonelik başarısız.');
    } finally { setLoading(false); }
  };

  const phone = settings.site_phone || SITE.phone;
  const mail = settings.site_email || SITE.email;
  const address = settings.site_address || SITE.address;
  const aboutText = settings.footer_about || `${SITE.fullName} — çocuklarımız ve gençlerimiz için eğitimde fırsat eşitliği yaratmak üzere kurulmuştur.`;

  const socials = [
    { Icon: Facebook, url: settings.social_facebook },
    { Icon: XLogo, url: settings.social_twitter },
    { Icon: Instagram, url: settings.social_instagram },
    { Icon: Youtube, url: settings.social_youtube },
  ].filter(s => s.url && s.url.trim() && s.url !== '#');

  return (
    <footer className="bg-ink-600 text-cream relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, #C9A96E 0, transparent 50%), radial-gradient(circle at 80% 80%, #E8826F 0, transparent 50%)' }} />

      {/* Newsletter strip */}
      <div className="border-b border-cream/10 relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <InlineText settingKey="footer_newsletter_kicker" fallback="Bültenimize Katılın" as="div"
              className="text-[11px] tracking-[0.28em] uppercase text-gold font-semibold" />
            <InlineText settingKey="footer_newsletter_title"
              fallback="Yeni projelerimiz, duyurular ve hikâyelerden haberdar ol."
              as="h3" multiline
              className="font-display text-3xl md:text-4xl mt-3 leading-tight" />
          </div>
          <form onSubmit={subscribe} className="flex items-stretch gap-3 bg-cream/5 border border-cream/15 rounded-full p-1.5 pl-6">
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="E-posta adresiniz" className="flex-1 bg-transparent outline-none text-cream placeholder:text-cream/50 text-sm" data-testid="newsletter-email" />
            <button disabled={loading} data-testid="newsletter-submit" className="inline-flex items-center gap-2 bg-gold text-ink-700 rounded-full px-6 h-11 font-semibold text-sm hover:bg-gold-400 transition-colors disabled:opacity-60">
              {loading ? '...' : 'Abone Ol'} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-2 lg:grid-cols-12 gap-10">
        {/* Brand */}
        <div className="lg:col-span-4">
          <Link to="/" className="inline-block" aria-label="YEKÜV">
            <img
              src="/logos/yekuv-logo.png"
              alt="YEKÜV"
              className="h-28 md:h-32 w-auto object-contain select-none"
              style={{ filter: 'brightness(0) invert(1)' }}
              draggable="false"
            />
          </Link>
          <p className="mt-6 text-cream/70 text-sm leading-relaxed max-w-sm">
            <InlineText settingKey="footer_about" fallback={aboutText} as="span" multiline />
          </p>

          {socials.length > 0 && (
            <div className="flex items-center gap-2 mt-6">
              {socials.map((s, i) => {
                const Icon = s.Icon;
                return (
                  <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`footer-social-${i}`} className="w-10 h-10 rounded-full border border-cream/15 grid place-items-center text-cream/80 hover:bg-gold hover:text-ink-700 hover:border-gold transition-colors">
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          )}
        </div>

        {/* Links */}
        <div className="lg:col-span-2">
          <h4 className="text-sm font-semibold text-gold tracking-wide mb-4">Kurumsal</h4>
          <ul className="space-y-2.5 text-sm text-cream/70">
            <li><Link to="/kurumsal" className="hover:text-cream">Tarihçe</Link></li>
            <li><Link to="/kurumsal/baskanlar" className="hover:text-cream">Başkanlarımız</Link></li>
            <li><Link to="/kurumsal#yonetim" className="hover:text-cream">Yönetim Kurulu</Link></li>
            <li><Link to="/kurumsal/kurullar" className="hover:text-cream">Kurullarımız</Link></li>
            <li><Link to="/kurumsal/medya" className="hover:text-cream">Medyada YEKÜV</Link></li>
            <li><Link to="/kurumsal/yayinlar" className="hover:text-cream">Yayınlarımız</Link></li>
            <li><Link to="/kurumsal/okul" className="hover:text-cream">Nilüfer Gökay Okulu</Link></li>
          </ul>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-sm font-semibold text-gold tracking-wide mb-4">Destek</h4>
          <ul className="space-y-2.5 text-sm text-cream/70">
            <li><Link to="/bagis/turler" className="hover:text-cream">Bağış Türleri</Link></li>
            <li><Link to="/bagis/ozel-gun" className="hover:text-cream">Özel Gün Bağışı</Link></li>
            <li><Link to="/bagis/kanallar" className="hover:text-cream">Bağış Kanalları</Link></li>
            <li><Link to="/burslar" className="hover:text-cream">Burs Başvuru</Link></li>
            <li><Link to="/gonullu" className="hover:text-cream">Gönüllü Ol</Link></li>
            <li><Link to="/kurumsal/destekciler" className="hover:text-cream">Destekçilerimiz</Link></li>
            <li><Link to="/kurumsal/vergi" className="hover:text-cream">Vergi Muafiyeti</Link></li>
          </ul>
        </div>

        <div className="lg:col-span-4">
          <h4 className="text-sm font-semibold text-gold tracking-wide mb-4">İletişim</h4>
          <ul className="space-y-3 text-sm text-cream/70">
            <li className="flex gap-3"><MapPin className="w-4 h-4 mt-0.5 text-gold shrink-0" />
              <InlineText settingKey="site_address" fallback={address} as="span" multiline />
            </li>
            <li className="flex gap-3"><Phone className="w-4 h-4 mt-0.5 text-gold shrink-0" />
              <InlineText settingKey="site_phone" fallback={phone} as="span" />
            </li>
            <li className="flex gap-3"><Mail className="w-4 h-4 mt-0.5 text-gold shrink-0" />
              <InlineText settingKey="site_email" fallback={mail} as="span" />
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-cream/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-cream/55">
          <div>© {new Date().getFullYear()} {SITE.fullName}. Tüm hakları saklıdır.</div>
          <div className="flex items-center gap-5">
            <Link to="/kurumsal/kvkk" className="hover:text-cream">KVKK</Link>
            <Link to="/kurumsal/vergi" className="hover:text-cream">Vergi Muafiyeti</Link>
            <Link to="/kurumsal/kvkk" className="hover:text-cream">Gizlilik</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
